import { PEOPLE_SEARCH_API_CHANGES } from '@aibees/elephant/constants';
import { FILTER_VALUE_SEPARATOR } from '@aibees/elephant/constants/filters';
import { useSearchQueryFilter } from '@aibees/elephant/hooks';
import { HttpMethod, PaginationResponse } from '@aibees/shared/definition';
import { useDebouncedMutation } from '@aibees/shared/hooks';
import {
  baseQueryClient,
  useBaseMutation,
  useBaseQuery,
} from '@aibees/shared/http';
import { toNumber } from '@aibees/shared/utils';
import { useWorkspace } from '@aibees/shared/workspace';
import { useCallback, useEffect, useRef } from 'react';
import {
  CompanyRequestType,
  CompanySearchBody,
} from '../../search-table-company/hooks/types';
import { useSearchPersonStore } from '../store';
import { PersonChangesInfo } from '../types';

export function useBrowseUpdatedPeople() {
  const { params } = useSearchQueryFilter();
  const workspace = useWorkspace()?.id;
  const { peopleSearchItems } = useSearchPersonStore();
  const tableIds = peopleSearchItems.map((item) => item.id);

  // Create a ref to always store the latest tableIds
  const tableIdsRef = useRef(tableIds);
  useEffect(() => {
    tableIdsRef.current = tableIds;
  }, [tableIds]);

  // Ensure there is a query with key 'useBrowseUpdatedPeople.name'
  // we don't use this query directly and just using it for a cached store for updated data after mutation
  useBaseQuery(
    [useBrowseUpdatedPeople.name],
    {},
    {
      enabled: false,
    }
  );

  const mutation = useBaseMutation<
    CompanySearchBody & { page: number; size: number },
    PaginationResponse<PersonChangesInfo>
  >(
    {
      url: PEOPLE_SEARCH_API_CHANGES.replace(':wid', String(workspace)),
      method: HttpMethod.post,
    },
    {
      onSuccess: (data) => {
        // Store result in cache
        baseQueryClient.setQueryData<PaginationResponse<PersonChangesInfo>>(
          [useBrowseUpdatedPeople.name],
          data
        );
      },
    }
  );

  const debouncedMutation = useDebouncedMutation(
    mutation,
    useBrowseUpdatedPeople.name,
    1000
  );

  return {
    ...mutation,
    mutate: useCallback(() => {
      const payload = {
        page: 0,
        size: toNumber(params?.size) ?? 25,
        contact: {
          id: {
            any: { include: tableIdsRef.current },
          },
        },
        account: {},
        type: CompanyRequestType.SIMILARITY,
        value: params?.value?.split(FILTER_VALUE_SEPARATOR),
      };
      return debouncedMutation.debouncedMutate(payload);
    }, [debouncedMutation, params, tableIdsRef]),
  };
}
